/**
 * 사업자등록증 OCR
 * Primary: CLOVA OCR (네이버 클라우드, 한국어 정확도 우수)
 * Fallback: Google Vision API (CLOVA 실패 시)
 */

const CLOVA_INVOKE_URL = process.env.REACT_APP_CLOVA_OCR_INVOKE_URL;
const CLOVA_SECRET_KEY = process.env.REACT_APP_CLOVA_OCR_SECRET_KEY;
const VISION_API_KEY = process.env.REACT_APP_GOOGLE_VISION_API_KEY;
const VISION_API_URL = `https://vision.googleapis.com/v1/images:annotate?key=${VISION_API_KEY}`;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const fileExt = (file) => {
  const m = (file.name || '').match(/\.([^.]+)$/);
  if (m) return m[1].toLowerCase();
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') return 'jpg';
  return 'jpg';
};

// CLOVA OCR — General Template
const ocrViaClova = async (file) => {
  if (!CLOVA_INVOKE_URL || !CLOVA_SECRET_KEY) throw new Error('CLOVA OCR 키 미설정');
  const base64 = await fileToBase64(file);
  const ext = fileExt(file);
  const message = {
    version: 'V2',
    requestId: `ieum-${Date.now()}`,
    timestamp: Date.now(),
    images: [{ format: ext, name: 'license', data: base64 }],
  };
  const res = await fetch(CLOVA_INVOKE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-OCR-SECRET': CLOVA_SECRET_KEY,
    },
    body: JSON.stringify(message),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    console.error('CLOVA OCR 오류:', res.status, data);
    throw new Error(data?.message || `CLOVA OCR 오류 (${res.status})`);
  }
  const fields = data?.images?.[0]?.fields || [];
  if (!fields.length) {
    throw new Error('CLOVA에서 텍스트를 인식하지 못했습니다.');
  }
  // fields[].inferText 를 줄바꿈으로 합쳐서 한 덩어리 텍스트로
  return fields.map((f) => f.inferText || '').join('\n');
};

// Google Vision OCR (fallback)
const ocrViaVision = async (file) => {
  if (!VISION_API_KEY) throw new Error('Vision API 키 미설정');
  const base64 = await fileToBase64(file);
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  const url = isPdf
    ? `https://vision.googleapis.com/v1/files:annotate?key=${VISION_API_KEY}`
    : VISION_API_URL;
  const body = isPdf
    ? {
      requests: [{
        inputConfig: { content: base64, mimeType: 'application/pdf' },
        features: [{ type: 'DOCUMENT_TEXT_DETECTION' }],
        imageContext: { languageHints: ['ko'] },
      }],
    }
    : {
      requests: [{
        image: { content: base64 },
        features: [{ type: 'DOCUMENT_TEXT_DETECTION' }],
        imageContext: { languageHints: ['ko'] },
      }],
    };
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const apiErr = data?.error?.message || data?.responses?.[0]?.error?.message;
    console.error('Vision API 오류:', res.status, apiErr, data);
    throw new Error(apiErr || `Vision API 오류 (${res.status})`);
  }
  const r0 = data?.responses?.[0];
  const fullText = isPdf
    ? (r0?.responses?.[0]?.fullTextAnnotation?.text || '')
    : (r0?.fullTextAnnotation?.text || '');
  if (!fullText) {
    throw new Error('Vision: 이미지에서 텍스트를 인식하지 못했습니다.');
  }
  return fullText;
};

export const ocrBusinessLicense = async (file) => {
  // CLOVA가 PDF 미지원 시 Vision 폴백
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  if (isPdf) {
    return ocrViaVision(file);
  }
  // 이미지: CLOVA 우선, 실패 시 Vision 폴백
  try {
    return await ocrViaClova(file);
  } catch (clovaErr) {
    console.warn('CLOVA OCR 실패, Vision 으로 폴백:', clovaErr?.message);
    return ocrViaVision(file);
  }
};

const BIZ_NO_PATTERN = /(\d{3})[-\s]?(\d{2})[-\s]?(\d{5})/g;

export const extractBusinessNumbers = (text) => {
  if (!text) return [];
  const found = new Set();
  for (const m of text.matchAll(BIZ_NO_PATTERN)) {
    found.add(`${m[1]}${m[2]}${m[3]}`);
  }
  return Array.from(found);
};

const stripDigits = (v) => String(v ?? '').replace(/\D/g, '');

// ───── 사업자등록증 필드 파싱 ─────

const detectBusinessTypeFromHeader = (text) => {
  const compact = text.replace(/\s+/g, '');
  if (/법인사업자/.test(compact)) return 'corporation';
  if (/일반과세자|간이과세자|면세사업자|개인사업자/.test(compact)) return 'sole_proprietor';
  return '';
};

const extractRepresentative = (text) => {
  // "성명(대표자) : 홍길동" / "대표자 : 홍길동" / "성명 : 홍길동"
  const m = text.match(/(?:대표자|성\s*명)[\s()가-힣]*[:：]\s*([가-힣]{2,5}(?:[\s,()][가-힣]{2,5})?)/);
  if (m) return m[1].trim().split(/[,()]/)[0].trim();
  return '';
};

const extractBusinessName = (text) => {
  // "상호 : ○○○" / "법인명(단체명) : 주식회사 ○○○"
  const m1 = text.match(/(?:법인명|단체명|상\s*호|업체명)[\s()가-힣]*[:：]\s*([^\n]+)/);
  if (m1) {
    const v = m1[1].trim();
    // "주식회사 ○○○" 같은 패턴 그대로, 다른 라벨 만나면 끊기
    return v.split(/(?:대표자|성\s*명|개업|법인등록|등록번호)/)[0].trim();
  }
  return '';
};

const extractOpenDate = (text) => {
  // "개업연월일 : 2020 년 05 월 11 일" / "2020-05-11" / "2020.05.11"
  const m1 = text.match(/개업[가-힣\s]*[:：]?\s*(\d{4})\s*[년.\-/]\s*(\d{1,2})\s*[월.\-/]\s*(\d{1,2})/);
  if (m1) return `${m1[1]}${m1[2].padStart(2, '0')}${m1[3].padStart(2, '0')}`;
  // 폴백: "개업" 글자 근처에 YYYYMMDD
  const m2 = text.match(/개업[^\d]{0,15}(\d{4})(\d{2})(\d{2})/);
  if (m2) return `${m2[1]}${m2[2]}${m2[3]}`;
  return '';
};

const extractAddress = (text) => {
  // "사업장 소재지 : 서울특별시 ..."
  const m = text.match(/(?:사업장\s*소재지|소재지)\s*[:：]?\s*([가-힣][^\n]+?)(?=\s*(?:본점|사업의|업태|종목|교부|공동|사업자단위|법인등록|대표자|성\s*명)|\n|$)/);
  if (m) return m[1].trim();
  // 폴백: 한국 주소 패턴 직접
  const m2 = text.match(/([가-힣]+(?:특별시|광역시|특별자치시|특별자치도|도)\s+[가-힣]+(?:시|군|구)\s+[가-힣\d\s\-,.()길로동읍면리번지]+?)(?=\s*(?:본점|사업의|업태|종목)|\n|$)/);
  return m2 ? m2[1].trim() : '';
};

export const parseBusinessLicenseFields = (text) => {
  if (!text) return {};
  const numbers = extractBusinessNumbers(text);
  const headerType = detectBusinessTypeFromHeader(text);
  const businessNumber = numbers[0] || '';
  // 사업자번호 가운데 2자리 보조 판별
  let businessType = headerType;
  if (!businessType && businessNumber) {
    const mid = parseInt(businessNumber.slice(3, 5), 10);
    businessType = mid >= 80 ? 'corporation' : 'sole_proprietor';
  }
  return {
    businessNumber,
    businessName: extractBusinessName(text),
    representativeName: extractRepresentative(text),
    openDate: extractOpenDate(text), // YYYYMMDD
    address: extractAddress(text),
    businessType, // corporation | sole_proprietor
  };
};

export const ocrAndExtractBusinessNumber = async (file, expectedBizNo) => {
  const text = await ocrBusinessLicense(file);
  const numbers = extractBusinessNumbers(text);
  const expected = stripDigits(expectedBizNo);
  const fields = parseBusinessLicenseFields(text);

  if (!numbers.length) {
    return { ok: false, reason: 'NO_BIZNO_FOUND', text, numbers: [], fields };
  }
  if (expected && numbers.includes(expected)) {
    return { ok: true, businessNumber: expected, text, numbers, fields: { ...fields, businessNumber: expected } };
  }
  if (!expected) {
    return { ok: true, businessNumber: numbers[0], text, numbers, fields };
  }
  return { ok: false, reason: 'BIZNO_MISMATCH', text, numbers, fields };
};

const VERIFY_URL = 'https://us-central1-ieum-8fd99.cloudfunctions.net/verifyBusinessLicense';
const CHECK_BIZNO_URL = 'https://us-central1-ieum-8fd99.cloudfunctions.net/checkBusinessNumber';

// 가입 전 사전 검증 — DB 쓰기 없이 NTS 상태만 확인
export const checkBusinessNumberRemote = async (businessNumber) => {
  const res = await fetch(CHECK_BIZNO_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ businessNumber: stripDigits(businessNumber) }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) {
    // 일시 오류는 통과 (관리자 추후 확인)
    return { allowed: true, warning: true, message: '검증 일시 오류 — 통과시킵니다.' };
  }
  return data;
};

export const verifyBusinessLicenseRemote = async ({ partnerId, businessNumber, licenseUrl, ocrFields, ocrFailed, collection }) => {
  const res = await fetch(VERIFY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partnerId,
      businessNumber: stripDigits(businessNumber),
      licenseUrl,
      ocrFields: ocrFields || null,
      ocrFailed: !!ocrFailed,
      collection: collection || 'partners',
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `검증 요청 실패 (${res.status})`);
  return data;
};
